import {
    PayloadAction,
    createAsyncThunk,
    createEntityAdapter,
    createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import * as biometricService from '../../service/biometric.service';
import {
    BiometricDevice,
    BiometricDeviceRequest,
} from '../../service/model/biometric.model';
import { showNotification } from '../alerts/alert.slice';
import { NotificationType } from '../../shared/enums';
import { getAxiosErrorMessage } from '../../service/axios/axios.call.service';

export interface BiometricDeviceState {
    isLoading: boolean;
}

const biometricDevicesAdapter = createEntityAdapter<BiometricDevice>({
    sortComparer: (a, b) => a.deviceName.localeCompare(b.deviceName),
});

export const initialState = biometricDevicesAdapter.getInitialState({
    isLoading: false,
} as BiometricDeviceState);

export const fetchBiometricDevices = createAsyncThunk<BiometricDevice[]>(
    'biometricDevices/all',
    async (_, { rejectWithValue }) => {
        return biometricService.getBiometricDevices().catch((errors) => {
            return rejectWithValue(errors);
        });
    },
);

export const createBiometricDevice = createAsyncThunk<
    BiometricDevice,
    BiometricDeviceRequest
>('biometricDevices/create', async (request, { rejectWithValue, dispatch }) => {
    return biometricService
        .createBiometricDevice(request)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Biometric device created successfully',
                    type: NotificationType.Success,
                }),
            );
            dispatch(fetchBiometricDevices());
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                }),
            );
            return rejectWithValue(errors);
        });
});

export const updateBiometricDevice = createAsyncThunk<
    BiometricDevice,
    BiometricDeviceRequest
>('biometricDevices/update', async (request, { rejectWithValue, dispatch }) => {
    return biometricService
        .updateBiometricDevice(request)
        .then((response) => {
            dispatch(
                showNotification({
                    message: 'Biometric device updated successfully',
                    type: NotificationType.Success,
                }),
            );
            dispatch(fetchBiometricDevices());
            return response;
        })
        .catch((errors) => {
            dispatch(
                showNotification({
                    message: getAxiosErrorMessage(errors.response),
                    type: NotificationType.Error,
                }),
            );
            return rejectWithValue(errors);
        });
});

export const deleteBiometricDevice = createAsyncThunk<number, number>(
    'biometricDevices/delete',
    async (id, { rejectWithValue, dispatch }) => {
        return biometricService
            .deleteBiometricDevice(id)
            .then((response) => {
                dispatch(
                    showNotification({
                        message: 'Biometric device deleted successfully',
                        type: NotificationType.Success,
                    }),
                );
                dispatch(fetchBiometricDevices());
                return response;
            })
            .catch((errors) => {
                dispatch(
                    showNotification({
                        message: getAxiosErrorMessage(errors.response),
                        type: NotificationType.Error,
                    }),
                );
                return rejectWithValue(errors);
            });
    },
);

const biometricDeviceSlice = createSlice({
    name: 'biometricDevices',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchBiometricDevices.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                fetchBiometricDevices.fulfilled,
                (state, action: PayloadAction<BiometricDevice[]>) => {
                    biometricDevicesAdapter.setAll(state, action.payload);
                    state.isLoading = false;
                },
            )
            .addCase(fetchBiometricDevices.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(createBiometricDevice.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                createBiometricDevice.fulfilled,
                (state, action: PayloadAction<BiometricDevice>) => {
                    biometricDevicesAdapter.addOne(state, action.payload);
                    state.isLoading = false;
                },
            )
            .addCase(createBiometricDevice.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(updateBiometricDevice.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                updateBiometricDevice.fulfilled,
                (state, action: PayloadAction<BiometricDevice>) => {
                    biometricDevicesAdapter.upsertOne(state, action.payload);
                    state.isLoading = false;
                },
            )
            .addCase(updateBiometricDevice.rejected, (state) => {
                state.isLoading = false;
            })
            .addCase(deleteBiometricDevice.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(
                deleteBiometricDevice.fulfilled,
                (state, action: PayloadAction<number>) => {
                    biometricDevicesAdapter.removeOne(state, action.payload);
                    state.isLoading = false;
                },
            )
            .addCase(deleteBiometricDevice.rejected, (state) => {
                state.isLoading = false;
            });
    },
});

export const {
    selectAll: selectBiometricDevices,
    selectById: selectBiometricDeviceById,
} = biometricDevicesAdapter.getSelectors<RootState>(
    (state) => state.biometricDevices,
);

export const biometricDeviceActions = biometricDeviceSlice.actions;

export const selectIsLoading = (state: RootState) =>
    state.biometricDevices?.isLoading;

export default biometricDeviceSlice.reducer;
